import { computed, watch } from 'vue';
import { Lang, Logger, Router } from '~/core';
import { hasAddon } from '~/plugins/addon';
import { globalMiddlewares } from '~/routes';
import { useModelsStore } from '~/stores/models';
import { useSettingsStore } from '~/stores/settings';
import { useUserStore } from '~/stores/user';

// Create page title computation function - will be called after Pinia is available
function createPageTitle() {
    return computed(() => {
        const settingsStore = useSettingsStore();
        const modelsStore = useModelsStore();
        const physgun = hasAddon('physgun');

        let branding = settingsStore.branding?.name || 'WISP';
        if (physgun && branding === 'WISP') branding = 'Physgun';

        const routeName = Router.currentRoute.value.name?.toString();
        const routeMatched = Router.currentRoute.value.matched;
        if (!routeName || routeMatched.length < 0) return `${branding} | Game Panel`;

        const routeTranslation = Lang.global.t(`navigation.${routeName}.title`);

        // TODO: this should also consider (Tabber)Passthrough with separation of admin and client area
        const route = routeMatched[routeMatched.length - 1];
        for (const path of route.path.split('/')) {
            if (!path.startsWith(':')) continue;

            const paramName = path.substring(1);
            const allModels = modelsStore.getAllModels;
            if (paramName in allModels) {
                const model = (allModels as any)[paramName];
                if (!model.name) continue;

                return `${branding} | ${model.name} - ${routeTranslation}`;
            }
        }
        return `${branding} | ${routeTranslation}`;
    });
}

// Initialize page title watching - will be called after Pinia is available
export function initializePageTitle() {
    const pageTitle = createPageTitle();
    watch(pageTitle, (value: string) => (document.title = value));
}

function getMiddlewares(routeMiddlewares: Middleware[] | undefined): Middleware[] {
    // Route middlewares run first: the global gates wait for the state backing
    // them (server model, user/settings data), and that state is produced by
    // route middlewares — ModelBindings fetches the models, Authenticated must
    // bounce logged-out visitors to login before a permission gate would wait
    // forever on user data that will never arrive.
    return [...(routeMiddlewares ?? []), ...globalMiddlewares];
}

function waitForInitialized(store: { isInitialized: boolean }): Promise<void> {
    return new Promise((resolve) => {
        if (store.isInitialized) return resolve();

        const unwatch = watch(
            () => store.isInitialized,
            (initialized) => {
                if (!initialized) return;

                unwatch();
                resolve();
            },
        );
    });
}

Router.beforeEach(async (to, from, next) => {
    const userStore = useUserStore();
    const settingsStore = useSettingsStore();

    if (!userStore.isInitialized || !settingsStore.isInitialized) {
        Logger.debug('Router', 'User and/or settings data is not available yet, waiting...');

        await Promise.all([waitForInitialized(userStore), waitForInitialized(settingsStore)]);
    }

    const middlewares = getMiddlewares(to.meta?.middlewares as Middleware[] | undefined);
    Logger.debug('Router', `Visit ${from.fullPath} -> ${to.fullPath} [middlewares: ${middlewares.map((a) => a.name()).join(', ')}]`);

    for (const middleware of middlewares) {
        const res: any = await middleware.run(to, from);
        if (res !== undefined) {
            Logger.debug('Router', `Middleware ${middleware.name()} failed.`);
            return next(res);
        }
    }

    return next();
});

Router.afterEach(async (to) => {
    const middlewares = getMiddlewares(to.meta?.middlewares as Middleware[] | undefined);
    for (const middleware of middlewares) {
        if (middleware.postRun) await middleware.postRun();
    }
});
